import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserButtonConfig, PhaserButton } from "src/games/shared/Button";
import { MainScene } from "../../../CollageGame/scenes/MainScene";
import TranslateApi from "src/utils/TranslateHelper";
import { Color } from "src/games/shared/ColorValue";
import { TiledGameObject } from "../../../shared/phaserHelpers/HandyTypes";

export class HowToPlayScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;
    private skyColor: number = 0x6eb8e5;

    hollandCode: "r" | "i" | "a" | "s" | "e" | "c" = "r";
    gameObjectiveTextKey: string = "Landscaping.Phaser.HowToPlay.objectiveText";
    howToPlayOnComputerTextKey: string = "Landscaping.Phaser.HowToPlay.computerInstructions";
    howToPlayOnTabletTextKey: string = "Landscaping.Phaser.HowToPlay.tabletInstructions";
    buttonTextKey: string = "Landscaping.Phaser.HowToPlay.getStarted";

    gameScene: typeof Phaser.Scene = MainScene;

    constructor() {
        super({
            key: "HowToPlay"
        });
    }

    preload(): void {
        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.tilemapTiledJSON('intro-map', require("/src/assets/qci/maps/intro_scene.json"));
        // Loading only the needed terrain
        switch (this.hollandCode) {
            case this.hollandCode = "r": {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/r.png"));
            }
            case this.hollandCode = "i": {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/i.png"));
            }
            case this.hollandCode = "a": {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/a.png"));
            }
            case this.hollandCode = "s": {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/s.png"));
            }
            case this.hollandCode = "e": {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/e.png"));
            }
            case this.hollandCode = "c": {
                this.load.image('terrain', require("/src/assets/qci/backgrounds/terrains/c.png"));
            }
        }
        this.load.atlas("pilots", require("/src/assets/qci/atlas/starPilotAll.png"), require("/src/assets/qci/atlas/starPilotAll.json"));
        this.load.atlas("starPods", require("/src/assets/qci/atlas/starPodAll.png"), require("/src/assets/qci/atlas/starPodAll.json"));
    }

    create(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        bindAllOfThis(this, HowToPlayScene.prototype);
        var instructionsTextKey;

        this.map = this.make.tilemap({ key: "intro-map" });

        let sky = this.add.image(0, 0, "pixel");
        sky.setOrigin(0);
        sky.setDisplaySize(this.gameWidth, this.gameHeight);
        sky.setTint(this.skyColor, this.skyColor, 0xffffff, 0xffffff); // Gradient sky!

        // Optional - only show instructions for the device you're on
        // Supports iPads, Android tablets, Kindle Fire tablets
        var isTablet = (/iPad|Android|Mobi|Silk/i.test(navigator.userAgent));

        if (this.sys.game.device.input.touch) {
            if (isTablet) {
                instructionsTextKey = this.howToPlayOnTabletTextKey;
            }
            else {
                // Touch screen computer
                instructionsTextKey = [this.howToPlayOnComputerTextKey, this.howToPlayOnTabletTextKey];
            }
        }
        else {
            instructionsTextKey = this.howToPlayOnComputerTextKey;
        }

        this.map.getObjectLayer("Objects").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "text") {
                // One text box for the game objective and how to play instructions
                this.addDialogBoxWithButton(ob, [this.gameObjectiveTextKey].concat(instructionsTextKey));
            }
            else if (ob.name === "pod") {
                var starPod = this.add.image(ob.x, ob.y, "starPods", this.hollandCode);
                starPod.setOrigin(0);
                starPod.setDepth(2);
                starPod.setDisplaySize(ob.width, ob.height);
            }
            else if (ob.name === "pilot") {
                var starPilot = this.add.image(ob.x, ob.y, "pilots", this.hollandCode);
                starPilot.setOrigin(0);
                starPilot.setDisplaySize(ob.width, ob.height);
                starPilot.setDepth(3);
            }
            else if (ob.name === "terrain") {
                let terrain = this.add.image(ob.x, ob.y, "terrain");
                terrain.setScale(0.8);
                terrain.setDepth(0);
                terrain.setOrigin(0);
            }
        });
    }

    /**
     * Creates a nice looking text box with multiple paragraphs and a button
     * @param translateKeys array of strings - these are the translation file keys of each paragraph you want in the text box
     */
    addDialogBoxWithButton(ob: TiledGameObject, translateKeys: string[]): void {
        // Feel free to edit these variables if the text isn't looking nice
        var padding = 30;
        var fontSize = 25;
        var borderWidth = 10;
        var borderColor = Color.PLANET_COLORS.get(this.hollandCode.toUpperCase());
        var verticalSpacing = padding;
        var paragraphSpacing = 20;
        var textBox = this.add.container(ob.x, ob.y);
        var paragraphs = [];
        var textStyle = { fontFamily: 'Roboto Mono, Arial', color: "#F8E71C", fontSize: fontSize, padding: 10 };
        var buttonColor = Color.KUDER_PRIMARY_BUTTON;
        var buttonTextColor = 0xffffff;

        // Add all the paragraphs to one big textbox
        translateKeys.forEach((key: string) => {
            var translatedText = TranslateApi.translate(key);
            // Split the translated text by newlines to create paragraphs
            translatedText.split("|").map(x => x.trim()).forEach((para: string) => {
                var paragraph = this.add.text(padding, verticalSpacing, para, textStyle);
                paragraph.setWordWrapWidth(ob.width - 2 * padding, true);
                paragraphs.push(paragraph);
                verticalSpacing += paragraph.height + paragraphSpacing;
                textBox.setSize(Math.max(ob.width, paragraph.width), verticalSpacing);
            });
        });

        var config: PhaserButtonConfig = {
            backgroundColor: buttonColor,
            smartSize: true
        };

        // Button to start your game
        var buttonText = TranslateApi.translate(this.buttonTextKey);
        var button = new PhaserButton(this, 0, 0, 200, 50, buttonText, config);
        textBox.setSize(textBox.width, textBox.height + button.displayHeight + 2 * padding);

        button.setPosition(padding + textStyle.padding, textBox.displayHeight - button.displayHeight - padding);
        button.on('buttonClicked', () => {
            this.scene.remove(this);
            this.scene.add("GameScene", this.gameScene, true);
        });

        // Add text box background
        var textBoxBackground = this.add.image(0, 0, "pixel");
        textBoxBackground.setOrigin(0);
        textBoxBackground.setDisplaySize(textBox.displayWidth, textBox.displayHeight);
        textBoxBackground.setTint(0x000000);
        textBoxBackground.setPosition(borderWidth + padding + textStyle.padding, borderWidth + padding + textStyle.padding);

        // Add text box border
        var textBoxBorder = this.add.image(0, 0, "pixel");
        textBoxBorder.setOrigin(0);
        textBoxBorder.setDisplaySize(textBoxBackground.displayWidth + 2 * borderWidth, textBoxBackground.displayHeight + 2 * borderWidth);
        textBoxBorder.setTint(borderColor);
        textBoxBackground.setPosition(borderWidth, borderWidth);

        textBox.add(textBoxBorder);
        textBox.add(textBoxBackground);
        textBox.add(paragraphs);
        textBox.add(button);
        textBox.setDepth(2);
    }
}



// WEBPACK FOOTER //
// ./src/games/demos/howToPlayScene/scenes/HowToPlay.ts